import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useOptions } from "../hooks/useOptions";
import { useFormConfig } from "../hooks/useFormConfig";
import { submitRegistration } from "../services/submitRegistration";
import "./RegistrationForm.css";

const renderWithBold = (text) => {
    if (!text) return null;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, index) => 
        index % 2 === 1 ? <strong key={index}>{part}</strong> : part
    );
};

export default function RegistrationForm() {
    const { formId = "savionalia-form" } = useParams();
    const { options, loading: optionsLoading, error: optionsError } = useOptions(formId);
    const { config, loading: configLoading, error: configError } = useFormConfig(formId);

    const [formData, setFormData] = useState({});
    const [selectedOption, setSelectedOption] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [formError, setFormError] = useState(null);

    // Set browser tab title from form config
    useEffect(() => {
        if (config?.title) {
            document.title = config.title;
        }
        return () => { document.title = "Salezjanie - formularze"; };
    }, [config?.title]);

    // Initialize formData when config loads
    useEffect(() => {
        if (config && config.fields) {
            const initial = {};
            config.fields.forEach((field) => {
                initial[field.id] = field.type === "checkbox" ? [] : "";
            });
            setFormData(initial);
        }
    }, [config]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (fieldId, option) => {
        setFormData((prev) => {
            const current = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
            const next = current.includes(option)
                ? current.filter((v) => v !== option)
                : [...current, option];
            return { ...prev, [fieldId]: next };
        });
    };

    const handleRadioChange = (fieldId, value) => {
        setFormData((prev) => ({ ...prev, [fieldId]: value }));
    };

    const handleOptionSelect = (optionId) => {
        setSelectedOption(optionId);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);

        if (!config || !config.fields) return;

        if (config.startTime && new Date() < new Date(config.startTime)) {
            setFormError("Formularz nie jest jeszcze dostępny.");
            return;
        }

        if (config.deadline && new Date() > new Date(config.deadline)) {
            setFormError("Termin zgłoszeń minął. Rejestracja jest zamknięta.");
            return;
        }

        // Validate required fields
        for (const field of config.fields) {
            if (field.required) {
                const val = formData[field.id];
                const empty = Array.isArray(val) ? val.length === 0 : !val?.trim();
                if (empty) {
                    setFormError(`Proszę wypełnić pole "${field.label}".`);
                    return;
                }
            }
        }

        if (!selectedOption) {
            setFormError("Proszę wybrać warsztat.");
            return;
        }

        setSubmitting(true);

        // Build email config — find the email field and first name field from form config
        let emailConfig = null;
        if (config?.emailEnabled && config?.emailMessage) {
            const emailField = config.fields?.find((f) => f.type === "email");
            const nameField = config.fields?.[0];
            emailConfig = {
                enabled: true,
                to: emailField ? formData[emailField.id] : null,
                subject: config.emailSubject || "Potwierdzenie rejestracji",
                message: config.emailMessage,
                name: nameField ? formData[nameField.id] : "",
                imageUrls: config.emailImageUrls || [],
            };
        }

        try {
            await submitRegistration(formId, formData, [selectedOption], emailConfig);
            setSubmitted(true);
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const loading = configLoading || optionsLoading;

    // Loading state
    if (loading) {
        return (
            <div className="form-container">
                <div className="form-card" style={{ textAlign: "center", padding: "3rem" }}>
                    <div className="spinner" style={{ margin: "0 auto 1rem" }}></div>
                    <p style={{ color: "#8a8580" }}>Ładowanie formularza...</p>
                </div>
            </div>
        );
    }

    // Config error
    if (configError) {
        return (
            <div className="form-container">
                <div className="form-card">
                    <div className="options-error">
                        Nie udało się załadować formularza: {configError}
                        <br />
                        <Link to="/" style={{ color: "white", marginTop: "1rem", display: "inline-block" }}>Wróć do listy formularzy</Link>
                    </div>
                </div>
            </div>
        );
    }

    if (!config) return null;

    // Start time check
    const isBeforeStart = config.startTime && new Date() < new Date(config.startTime);
    if (isBeforeStart) {
        return (
            <div className="form-container">
                <div className="form-card">
                    <div className="form-header">
                        <h1>{config.title || "Formularz rejestracji"}</h1>
                        {config.subtitle && (
                            <p className="form-subtitle">{config.subtitle}</p>
                        )}
                    </div>
                    <div className="form-not-open" role="status" style={{ marginTop: '1.5rem' }}>
                        <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}>🔒</span>
                        Formularz będzie dostępny od{" "}
                        <strong>{new Date(config.startTime).toLocaleString("pl-PL")}</strong>.
                    </div>
                </div>
            </div>
        );
    }

    // Deadline check
    const isPastDeadline = config.deadline && new Date() > new Date(config.deadline);
    if (isPastDeadline) {
        return (
            <div className="form-container">
                <div className="form-card">
                    <div className="form-header">
                        <h1>{config.title || "Formularz rejestracji"}</h1>
                        {config.subtitle && (
                            <p className="form-subtitle">{config.subtitle}</p>
                        )}
                    </div>
                    <div className="form-error" role="alert" style={{ marginTop: '1.5rem' }}>
                        <span className="error-icon">⏰</span>
                        Rejestracja została zakończona{" "}
                        {new Date(config.deadline).toLocaleString("pl-PL")}.
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (submitted) {
        const displayName = config.fields?.[0]
            ? formData[config.fields[0].id]
            : "";

        return (
            <div className="form-container">
                <div className="success-card">
                    <div className="success-icon">✓</div>
                    <h2>Rejestracja zakończona!</h2>
                    <p>
                        Dziękujemy{displayName ? `, ${displayName}` : ""}! Twoje zgłoszenie
                        zostało wysłane.
                    </p>
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            setSubmitted(false);
                            const initial = {};
                            config.fields.forEach((f) => (initial[f.id] = ""));
                            setFormData(initial);
                            setSelectedOption(null);
                        }}
                    >
                        Wyślij kolejne zgłoszenie
                    </button>
                </div>
            </div>
        );
    }

    // Group fields: consecutive halfWidth fields are paired
    const fieldRows = [];
    const fields = config.fields || [];
    let i = 0;
    while (i < fields.length) {
        if (
            fields[i].halfWidth &&
            i + 1 < fields.length &&
            fields[i + 1].halfWidth
        ) {
            fieldRows.push([fields[i], fields[i + 1]]);
            i += 2;
        } else {
            fieldRows.push([fields[i]]);
            i += 1;
        }
    }

    return (
        <div className="form-container">
            <div className="form-card">
                <div className="form-header">
                    <h1>{config.title || "Formularz rejestracji"}</h1>
                    {config.subtitle && (
                        <p className="form-subtitle">{config.subtitle}</p>
                    )}
                    {config.deadline && (
                        <p className="form-deadline">
                            <span style={{ marginRight: '0.35rem' }}>⏰</span>
                            Zgłoszenia przyjmowane do:{" "}
                            <strong>{new Date(config.deadline).toLocaleString("pl-PL")}</strong>
                        </p>
                    )}
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    {/* Dynamic Fields */}
                    <div className="form-section">
                        <h2 className="section-title">
                            <span className="section-icon">👤</span>
                            Dane osobowe
                        </h2>

                        {fieldRows.map((row, rowIndex) => {
                            if (row.length === 2) {
                                return (
                                    <div className="fields-grid" key={rowIndex}>
                                        {row.map((field) => (
                                            <div className="field-group" key={field.id}>
                                                <label>{field.label}</label>
                                                {field.type === "checkbox" ? (
                                                    <div className="checkbox-group">
                                                        {(field.options || []).map((opt) => {
                                                            const checked = (formData[field.id] || []).includes(opt);
                                                            return (
                                                                <label key={opt} className={`checkbox-item${checked ? " checked" : ""}`}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={checked}
                                                                        onChange={() => handleCheckboxChange(field.id, opt)}
                                                                    />
                                                                    <span className="checkbox-mark"></span>
                                                                    <span className="checkbox-option-label">{opt}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                ) : field.type === "radio" ? (
                                                    <div className="radio-group">
                                                        {(field.options || []).map((opt) => {
                                                            const selected = formData[field.id] === opt;
                                                            return (
                                                                <label key={opt} className={`radio-item${selected ? " selected" : ""}`}>
                                                                    <input
                                                                        type="radio"
                                                                        name={field.id}
                                                                        checked={selected}
                                                                        onChange={() => handleRadioChange(field.id, opt)}
                                                                    />
                                                                    <span className="radio-mark"></span>
                                                                    <span className="checkbox-option-label">{opt}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <input
                                                        id={field.id}
                                                        name={field.id}
                                                        type={field.type || "text"}
                                                        placeholder={field.placeholder || ""}
                                                        value={formData[field.id] || ""}
                                                        onChange={handleInputChange}
                                                        required={field.required}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                );
                            }
                            const field = row[0];
                            return (
                                <div className="field-group" key={field.id}>
                                    <label>{field.label}</label>
                                    {field.type === "checkbox" ? (
                                        <div className="checkbox-group">
                                            {(field.options || []).map((opt) => {
                                                const checked = (formData[field.id] || []).includes(opt);
                                                return (
                                                    <label key={opt} className={`checkbox-item${checked ? " checked" : ""}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => handleCheckboxChange(field.id, opt)}
                                                        />
                                                        <span className="checkbox-mark"></span>
                                                        <span className="checkbox-option-label">{opt}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    ) : field.type === "radio" ? (
                                        <div className="radio-group">
                                            {(field.options || []).map((opt) => {
                                                const selected = formData[field.id] === opt;
                                                return (
                                                    <label key={opt} className={`radio-item${selected ? " selected" : ""}`}>
                                                        <input
                                                            type="radio"
                                                            name={field.id}
                                                            checked={selected}
                                                            onChange={() => handleRadioChange(field.id, opt)}
                                                        />
                                                        <span className="radio-mark"></span>
                                                        <span className="checkbox-option-label">{opt}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <input
                                            id={field.id}
                                            name={field.id}
                                            type={field.type || "text"}
                                            placeholder={field.placeholder || ""}
                                            value={formData[field.id] || ""}
                                            onChange={handleInputChange}
                                            required={field.required}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Options Checklist */}
                    <div className="form-section">
                        <h2 className="section-title">
                            <span className="section-icon">📋</span>
                            Wybierz opcję
                        </h2>
                        <p className="section-description">
                            Wybierz opcję, na którą chcesz się zapisać. Liczba miejsc jest
                            ograniczona!
                        </p>

                        {optionsError && (
                            <div className="options-error">
                                Nie udało się załadować opcji: {optionsError}
                            </div>
                        )}

                        {!optionsError && (
                            <div className="options-list">
                                {options.map((option) => {
                                    const isFull = option.spotsLeft <= 0;
                                    const isSelected = selectedOption === option.id;
                                    const spotsPercent =
                                        option.totalSpots > 0
                                            ? (option.spotsLeft / option.totalSpots) * 100
                                            : 0;

                                    return (
                                        <label
                                            key={option.id}
                                            className={`option-card ${isSelected ? "selected" : ""} ${isFull ? "full" : ""
                                                }`}
                                        >
                                            <div className="option-checkbox">
                                                <input
                                                    type="radio"
                                                    name="workshop"
                                                    checked={isSelected}
                                                    disabled={isFull}
                                                    onChange={() => handleOptionSelect(option.id)}
                                                />
                                                <span className="checkmark"></span>
                                            </div>

                                            <div className="option-info">
                                                <span className="option-name" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere", lineHeight: "1.4" }}>{option.name}</span>
                                                {option.description && (
                                                    <p style={{ margin: "0.25rem 0 0.5rem", fontSize: "0.9rem", color: "#8a8580", lineHeight: "1.4", whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                                        {renderWithBold(option.description)}
                                                    </p>
                                                )}
                                                {((option.imageUrls && option.imageUrls.length > 0) ? option.imageUrls : (option.imageUrl ? [option.imageUrl] : [])).length > 0 && (
                                                    <div className="option-images-container" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                                        {((option.imageUrls && option.imageUrls.length > 0) ? option.imageUrls : (option.imageUrl ? [option.imageUrl] : [])).map((url, i) => (
                                                            <div key={i} className="option-image-wrapper" style={{ margin: 0 }}>
                                                                <img src={url} alt={`${option.name} ${i + 1}`} className="option-image" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginTop: "0.5rem" }}>
                                                    <div className="spots-bar-container" style={{ flex: 1, margin: 0 }}>
                                                        <div
                                                            className={`spots-bar ${spotsPercent <= 20
                                                                ? "critical"
                                                                : spotsPercent <= 50
                                                                    ? "warning"
                                                                    : ""
                                                                }`}
                                                            style={{ width: `${spotsPercent}%` }}
                                                        ></div>
                                                    </div>
                                                    <div className="spots-badge-wrapper" style={{ margin: 0, marginTop: 0 }}>
                                                        <span
                                                            className={`spots-badge ${isFull
                                                                ? "badge-full"
                                                                : option.spotsLeft <= 5
                                                                    ? "badge-critical"
                                                                    : option.spotsLeft <= 10
                                                                        ? "badge-warning"
                                                                        : ""
                                                                }`}
                                                        >
                                                            {isFull
                                                                ? "BRAK MIEJSC"
                                                                : `${option.spotsLeft} ${option.spotsLeft === 1
                                                                    ? "miejsce"
                                                                    : option.spotsLeft < 5
                                                                        ? "miejsca"
                                                                        : "miejsc"
                                                                }`}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Error Message */}
                    {formError && (
                        <div className="form-error" role="alert">
                            <span className="error-icon">⚠️</span>
                            {formError}
                        </div>
                    )}


                    {/* Submit Button */}
                    <button
                        type="submit"
                        className="btn btn-primary btn-submit"
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <span className="btn-spinner"></span>
                                Wysyłanie...
                            </>
                        ) : (
                            "Wyślij zgłoszenie"
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
